import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Seal, Snowflake, Drop, Leaf } from '@phosphor-icons/react';

const KEY_LOGO_URL = '/elemantra-key-logo.png';

export const WhySection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
    },
  };

  const features = [
    {
      icon: Seal,
      title: t.why.item1.title,
      desc: t.why.item1.desc,
    },
    {
      icon: Snowflake,
      title: t.why.item2.title,
      desc: t.why.item2.desc,
    },
    {
      icon: Drop,
      title: t.why.item3.title,
      desc: t.why.item3.desc,
    },
    {
      icon: Leaf,
      title: t.why.item4.title,
      desc: t.why.item4.desc,
    },
  ];

  return (
    <section
      id="why"
      ref={ref}
      className="relative py-24 md:py-40 bg-[#F3EFE6] overflow-hidden"
      data-testid="why-section"
    >
      {/* Watermark Key Logo - centered */}
      <div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ opacity: 0.03 }}
      >
        <img 
          src={KEY_LOGO_URL} 
          alt="" 
          className="w-[70%] max-w-3xl h-auto object-contain"
          aria-hidden="true"
        />
      </div>

      {/* Grain Texture */}
      <div 
        className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-multiply"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Header */}
          <div className="text-center mb-16 md:mb-24">
            <motion.span
              variants={itemVariants}
              className="text-xs tracking-[0.25em] uppercase text-[#B89C6A] font-outfit font-semibold"
            >
              {t.why.overline}
            </motion.span>
            
            <motion.h2
              variants={itemVariants}
              className="font-cormorant text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2A2624] mt-4"
            >
              {t.why.title}
            </motion.h2>
          </div>

          {/* Features Grid */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group text-center p-8 bg-[#FDFBF7] hover:bg-white transition-colors duration-500 border border-transparent hover:border-[#E5DED0]"
                data-testid={`why-feature-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#F3EFE6] group-hover:bg-[#E5DED0] flex items-center justify-center transition-colors duration-500">
                  <feature.icon 
                    size={32} 
                    weight="light"
                    className="text-[#B89C6A]"
                  />
                </div>
                <h3 className="font-cormorant text-xl text-[#2A2624] mb-3">
                  {feature.title}
                </h3>
                <p className="font-outfit text-sm text-[#5C5650] leading-relaxed">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
